/**
 * Route Renderer Component
 *
 * Maps route configuration to React Router Routes with lazy loading
 */

import { Routes, Route } from 'react-router-dom'
import { Suspense } from 'react'
import { PageShell } from '../components/PageShell'
import ProtectedRoute from '../components/ProtectedRoute'
import { RouteLoadingFallback } from '../components/RouteLoadingFallback'
import type { RouteConfig } from './config'
import { LazyComponent } from './LazyComponent'

interface RouterProps {
  /** Array of route configurations */
  routes: RouteConfig[]
}

/**
 * Maps route configs to React Router
 *
 * Handles:
 * - Lazy loading with Suspense
 * - Authentication protection
 * - PageShell wrapper with config
 * - Loading fallback UI
 */
export function Router({ routes }: RouterProps): JSX.Element {
  return (
    <Routes>
      {routes.map((route) => {
        // Determine the element based on configuration
        const element = createRouteElement(route)

        // Wrap in ProtectedRoute if auth required
        const protectedElement = route.auth !== false ? (
          <ProtectedRoute>{element}</ProtectedRoute>
        ) : (
          element
        )

        return <Route key={route.path} path={route.path} element={protectedElement} />
      })}

      {/* Catch-all 404 route */}
      <Route path="*" element={<NotFoundRoute />} />
    </Routes>
  )
}

/**
 * Create route element with appropriate shell and loading state
 */
function createRouteElement(route: RouteConfig): JSX.Element {
  const lazyComponent = (
    <LazyComponent component={route.component} pageTitle={route.title} fallback={<RouteLoadingFallback />} />
  )

  // Apply PageShell wrapper if configured
  if (route.shell === 'none') {
    // No shell wrapper (auth pages, callbacks, etc)
    return lazyComponent
  }

  if (route.shell === 'bgs') {
    // BGS Core shell with standard layout
    return (
      <PageShell
        config={{
          id: route.path.replace(/[^a-z0-9]/gi, '-'),
          title: route.title || 'Page',
          description: route.description || '',
          actions: [],
          layout: { containerWidth: 'xl', columns: 1 },
        }}
      >
        {lazyComponent}
      </PageShell>
    )
  }

  if (route.shell === 'escape-route') {
    // EscapeRoute custom shell
    return (
      <PageShell
        config={{
          id: route.path.replace(/[^a-z0-9]/gi, '-'),
          title: route.title || 'EscapeRoute',
          description: route.description || '',
          actions: [],
          layout: { containerWidth: 'full', columns: 1 },
        }}
      >
        {lazyComponent}
      </PageShell>
    )
  }

  // Default: just the component
  return lazyComponent
}

/**
 * 404 Not Found Route
 *
 * Displayed when user navigates to an unmapped route
 */
function NotFoundRoute(): JSX.Element {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold">404</h1>
        <p className="text-lg text-muted-foreground">Page not found</p>
        <a href="/" className="text-primary hover:underline">
          Return to home
        </a>
      </div>
    </div>
  )
}
